import { useState } from "react";
import { Plus, Pencil, Eye, EyeOff, ExternalLink, AlertTriangle, CheckCircle, Trash2, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, StatusBadge, fmtDate } from "./shared";
import { RecordEditor } from "./RecordEditor";

const len = (v) => `${String(v || "").length} chars`;
const BLOG_FIELDS = [
  { key: "title", label: "Title", required: true, hint: len },
  { key: "slug", label: "URL slug", required: true, lockOnEdit: true },
  { key: "excerpt", label: "Excerpt", type: "textarea", rows: 3, required: true, hint: len },
  { key: "body_markdown", label: "Article body (Markdown)", type: "textarea", rows: 14, required: true },
  { key: "meta_title", label: "SEO meta title (30–60 chars)", hint: len },
  { key: "meta_description", label: "SEO meta description (70–160 chars)", type: "textarea", rows: 2, hint: len },
  { key: "cover_image_url", label: "Cover image URL" },
  { key: "tags", label: "Tags", type: "list", rows: 3 },
  { key: "is_published", label: "Published", type: "switch" },
];

export const seoIssues = (p) => {
  const issues = [];
  const mt = (p.meta_title || "").length, md = (p.meta_description || "").length;
  if (!mt) issues.push("Missing meta title"); else if (mt < 30 || mt > 60) issues.push(`Meta title ${mt} chars`);
  if (!md) issues.push("Missing meta description"); else if (md < 70 || md > 160) issues.push(`Meta description ${md} chars`);
  if (!p.excerpt) issues.push("Missing excerpt");
  return issues;
};

const PostRow = ({ p, onEdit, onToggle, onDelete }) => {
  const issues = seoIssues(p);
  const id = `blog-${p.slug}`;
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-5 py-4 border-b border-slate-50 last:border-0" data-testid={id}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">{p.title}<StatusBadge status={p.is_published ? "published" : "draft"} testid={`${id}-status`} /></p>
        <p className="text-xs text-slate-500 mt-0.5">/{p.slug} · {p.views || 0} views · {p.published_at ? `Published ${fmtDate(p.published_at)}` : "Not published"}</p>
        <p className={`text-xs mt-1 flex items-center gap-1 ${issues.length ? "text-amber-600" : "text-emerald-600"}`} data-testid={`${id}-seo`}>
          {issues.length ? <><AlertTriangle className="h-3 w-3" />{issues.join(" · ")}</> : <><CheckCircle className="h-3 w-3" />SEO checks passed</>}
        </p>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`${id}-edit`}><Pencil className="h-3.5 w-3.5 mr-1" />Review</Button>
        <Button size="sm" variant={p.is_published ? "outline" : "default"} className={p.is_published ? "" : "bg-emerald-600 hover:bg-emerald-700"} onClick={onToggle} data-testid={`${id}-toggle`}>
          {p.is_published ? <><EyeOff className="h-3.5 w-3.5 mr-1" />Unpublish</> : <><Eye className="h-3.5 w-3.5 mr-1" />Publish</>}
        </Button>
        {p.is_published && <a href={`/commerce/insights/${p.slug}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid={`${id}-view`}><ExternalLink className="h-3.5 w-3.5" /></Button></a>}
        <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`${id}-delete`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
      </div>
    </div>
  );
};

export const CommerceBlog = ({ call }) => {
  const { data, loading, reload } = useLoad(call, "/blog");
  const [editing, setEditing] = useState(null);
  const posts = (Array.isArray(data) ? data : []).slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const patch = async (slug, body, msg) => { await call(`/blog/${encodeURIComponent(slug)}`, { method: "PATCH", body }); toast.success(msg); reload(); };
  const save = async (values) => {
    if (editing?.slug) await patch(editing.slug, values, "Article saved");
    else { await call("/blog", { method: "POST", body: values }); toast.success("Article created"); reload(); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await call(`/blog/${encodeURIComponent(p.slug)}`, { method: "DELETE" }); toast.success("Deleted"); reload();
  };
  const published = posts.filter(p => p.is_published).length;
  return (
    <Panel testid="commerce-blog" title={`Knowledge centre — ${published} published · ${posts.length - published} awaiting review`}
      action={<Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setEditing({})} data-testid="blog-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />New article</Button>}>
      {loading ? <Loading /> : posts.length ? posts.map(p => (
        <PostRow key={p.slug} p={p} onEdit={() => setEditing(p)} onDelete={() => remove(p)}
          onToggle={() => patch(p.slug, { is_published: !p.is_published }, p.is_published ? "Unpublished" : "Published")} />
      )) : <div className="p-6"><EmptyState icon={Newspaper} title="No articles yet" /></div>}
      <RecordEditor open={!!editing} onOpenChange={o => !o && setEditing(null)} title={editing?.slug ? "Review article" : "New article"}
        fields={BLOG_FIELDS} record={editing?.slug ? editing : null} onSave={save} testid="blog-editor" />
    </Panel>
  );
};
